import React, { useEffect, useRef, useState } from 'react';

interface Stat {
  value: number;
  suffix: string;
  label: string;
  description: string;
}

const statsData: Stat[] = [
  { value: 50, suffix: '+', label: 'Projets livrés', description: 'En production, maintenus et monitorés.' },
  { value: 25, suffix: '+', label: 'Clients partenaires', description: 'Dont plusieurs depuis nos débuts.' },
  { value: 99, suffix: '%', label: 'Taux de satisfaction', description: 'Mesuré projet par projet.' },
  { value: 5, suffix: '+', label: "Années d'expertise", description: 'En innovation numérique appliquée.' },
];

const Counter: React.FC<{ stat: Stat; triggered: boolean; delay: number }> = ({ stat, triggered, delay }) => {
  const [count, setCount] = useState(0);
  const hasRun = useRef(false);

  useEffect(() => {
    if (!triggered || hasRun.current) return;
    hasRun.current = true;
    const t = setTimeout(() => {
      const steps = 45;
      const stepVal = stat.value / steps;
      const interval = 1400 / steps;
      let cur = 0;
      const id = setInterval(() => {
        cur += stepVal;
        if (cur >= stat.value) {
          setCount(stat.value);
          clearInterval(id);
        } else {
          setCount(Math.floor(cur));
        }
      }, interval);
    }, delay);
    return () => clearTimeout(t);
  }, [triggered, stat.value, delay]);

  return (
    <>
      {count}
      {stat.suffix}
    </>
  );
};

const AnimatedStats: React.FC = () => {
  const [triggered, setTriggered] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setTriggered(true);
          obs.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="stats-section">
      {/* Top gold accent */}
      <div className="stats-accent-top" />

      <div className="stats-inner">
        {/* Header */}
        <div className="reveal stats-header">
          <div className="sec-tag" style={{ color: 'rgba(255,255,255,.55)' }}>
            L'impact en chiffres
          </div>
          <h2 className="stats-title">
            Des chiffres qui <em>parlent</em>
          </h2>
          <div className="stats-bar" />
        </div>

        {/* 4 stats grid */}
        <div className="stats-grid">
          {statsData.map((stat, i) => (
            <div key={stat.label} className={`reveal d${Math.min(i + 1, 4)} stats-cell`}>
              <div className="stats-value">
                <Counter stat={stat} triggered={triggered} delay={i * 110} />
              </div>
              <div className="stats-label">{stat.label}</div>
              <p className="stats-desc">{stat.description}</p>
              {/* Animated fill bar */}
              <div className="stats-fill">
                <div
                  className="stats-fill-inner"
                  style={{
                    width: triggered ? '100%' : '0%',
                    transitionDelay: `${i * 110 + 300}ms`,
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        .stats-section {
          padding: 5rem 0;
          background: var(--black);
          position: relative;
          overflow: hidden;
        }
        .stats-accent-top {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(184,146,42,.6), transparent);
        }
        .stats-inner {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 2rem;
        }
        .stats-header {
          margin-bottom: 3rem;
          text-align: center;
        }
        .stats-title {
          font-family: 'Cormorant Garamond', Georgia, serif;
          font-size: clamp(1.8rem, 3.5vw, 2.5rem);
          font-weight: 600;
          color: #FFFFFF;
          line-height: 1.2;
          margin: 0 0 .8rem;
        }
        .stats-title em {
          font-style: italic;
          color: var(--gold);
        }
        .stats-bar {
          width: 48px;
          height: 3px;
          background: var(--gold);
          margin: 1rem auto 0;
        }

        /* 4-column grid */
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 1px;
          background: rgba(255,255,255,.08);
        }
        .stats-cell {
          background: var(--black);
          padding: 2.5rem 1.8rem 2rem;
          display: flex;
          flex-direction: column;
          gap: .5rem;
          position: relative;
        }
        .stats-value {
          font-family: 'Cormorant Garamond', Georgia, serif;
          font-size: 3.5rem;
          font-weight: 600;
          color: #FFFFFF;
          line-height: 1;
          margin-bottom: .2rem;
        }
        .stats-label {
          font-family: 'Montserrat', sans-serif;
          font-size: .68rem;
          font-weight: 700;
          letter-spacing: .18em;
          text-transform: uppercase;
          color: var(--gold);
        }
        .stats-desc {
          font-family: 'Montserrat', sans-serif;
          font-size: .78rem;
          font-weight: 300;
          color: rgba(255,255,255,.55);
          line-height: 1.6;
          margin: 0;
          flex: 1;
        }
        .stats-fill {
          width: 100%;
          height: 2px;
          background: rgba(255,255,255,.08);
          margin-top: 1rem;
          overflow: hidden;
        }
        .stats-fill-inner {
          height: 100%;
          width: 0;
          background: linear-gradient(90deg, var(--gold), var(--blue-light));
          transition: width 1.3s cubic-bezier(.4, 0, .2, 1);
        }

        /* Responsive */
        @media (max-width: 900px) {
          .stats-grid { grid-template-columns: repeat(2, 1fr); }
        }
        @media (max-width: 520px) {
          .stats-section { padding: 3.5rem 0; }
          .stats-inner { padding: 0 1rem; }
          .stats-grid { grid-template-columns: 1fr; }
          .stats-cell { padding: 1.8rem 1.3rem 1.5rem; }
          .stats-value { font-size: 2.8rem; }
        }
      `}</style>
    </section>
  );
};

export default AnimatedStats;
